import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Field, Input, Textarea, SaveBar } from "./adminUi";
import { Plus, Trash2 } from "lucide-react";

const LOCATIONS = [
  { value: "head", label: "In <head> (before </head>)" },
  { value: "body_start", label: "Top of <body> (right after opening tag)" },
  { value: "body_end", label: "Bottom of <body> (before closing tag)" },
];

export default function AdminAnalytics() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const updateAnalytics = (k, v) =>
    setContent((c) => ({ ...c, analytics: { ...(c.analytics || {}), [k]: v } }));

  const scripts = content?.custom_scripts || [];
  const setScripts = (arr) => setContent((c) => ({ ...c, custom_scripts: arr }));
  const updateScript = (i, k, v) => {
    const next = [...scripts];
    next[i] = { ...next[i], [k]: v };
    setScripts(next);
  };
  const addScript = () =>
    setScripts([...scripts, { name: "New script", code: "", location: "head", enabled: true }]);
  const removeScript = (i) => setScripts(scripts.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Analytics & scripts updated");
      // Trigger a reload so scripts get re-injected with new content
      setTimeout(() => window.location.reload(), 800);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <div className="text-white/60">Loading…</div>;
  const a = content.analytics || {};

  return (
    <div data-testid="admin-analytics">
      <p className="text-xs uppercase tracking-[0.25em] text-fuchsia-400">Integrations</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-white md:text-4xl">Analytics & Scripts</h1>
      <p className="mt-3 text-white/60">Connect analytics and inject custom code into your site.</p>

      <div className="mt-10 rounded-2xl border border-white/10 bg-[#131B2F] p-8">
        <h2 className="text-lg font-semibold text-white">Google services</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Field label="Google Analytics 4 — Measurement ID" hint="Looks like G-XXXXXXXXXX. Get it from Analytics Admin → Data Streams → Web.">
            <Input
              value={a.google_analytics_id || ""}
              onChange={(e) => updateAnalytics("google_analytics_id", e.target.value.trim())}
              placeholder="G-XXXXXXXXXX"
              data-testid="ga-id-input"
            />
          </Field>
          <Field label="Google Tag Manager ID (optional)" hint="Looks like GTM-XXXXXX.">
            <Input
              value={a.google_tag_manager_id || ""}
              onChange={(e) => updateAnalytics("google_tag_manager_id", e.target.value.trim())}
              placeholder="GTM-XXXXXX"
              data-testid="gtm-id-input"
            />
          </Field>
          <Field
            label="Google Search Console verification"
            hint={`Only paste the "content" value from the meta tag Search Console gives you (not the whole tag).`}
          >
            <Input
              value={a.google_search_console_verification || ""}
              onChange={(e) => updateAnalytics("google_search_console_verification", e.target.value.trim())}
              placeholder="e.g. abc123DEFghijkl456MNOpqrstuvwxyz789"
              data-testid="gsc-input"
            />
          </Field>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-white/10 bg-[#131B2F] p-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Custom scripts / snippets</h2>
            <p className="mt-1 text-sm text-white/50">
              Paste any JS / HTML snippet (chat widgets, pixels, verification tags, etc.). Choose where it's injected.
            </p>
          </div>
          <button
            onClick={addScript}
            data-testid="script-add-btn"
            className="group relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white"
          >
            <span className="absolute inset-0 rounded-full gradient-bg" />
            <span className="relative flex items-center gap-1.5"><Plus size={12} /> Add script</span>
          </button>
        </div>

        <div className="mt-6 space-y-4">
          {scripts.length === 0 && (
            <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-sm text-white/40">
              No custom scripts yet. Click "Add script" to insert one.
            </div>
          )}
          {scripts.map((s, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-[#0F1528] p-5" data-testid={`script-row-${i}`}>
              <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto]">
                <Field label="Name (internal)">
                  <Input value={s.name} onChange={(e) => updateScript(i, "name", e.target.value)} placeholder="e.g. Meta Pixel" />
                </Field>
                <Field label="Location">
                  <select
                    value={s.location}
                    onChange={(e) => updateScript(i, "location", e.target.value)}
                    className="w-full rounded-lg border border-white/10 bg-[#0B1020] px-4 py-2.5 text-sm text-white"
                    data-testid={`script-location-${i}`}
                  >
                    {LOCATIONS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </Field>
                <div className="flex items-end gap-3">
                  <label className="mb-1.5 flex items-center gap-2 text-sm text-white/80">
                    <input
                      type="checkbox"
                      checked={!!s.enabled}
                      onChange={(e) => updateScript(i, "enabled", e.target.checked)}
                      data-testid={`script-enabled-${i}`}
                    />
                    Enabled
                  </label>
                  <button
                    onClick={() => removeScript(i)}
                    data-testid={`script-remove-${i}`}
                    className="mb-0.5 rounded-lg border border-white/10 p-2.5 text-white/60 hover:text-white"
                    aria-label="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <Field label="Code (HTML / <script>)">
                <Textarea
                  rows={5}
                  value={s.code}
                  onChange={(e) => updateScript(i, "code", e.target.value)}
                  placeholder={"<script>\n  // your code here\n</script>"}
                  data-testid={`script-code-${i}`}
                  className="font-mono text-xs"
                />
              </Field>
            </div>
          ))}
        </div>
      </div>

      <SaveBar onSave={save} saving={saving}>
        Site will reload after saving so scripts take effect.
      </SaveBar>
    </div>
  );
}
